/* eslint-disable complexity */
import {makeAutoObservable, computed, action, flow} from 'mobx';
import {fetchJobCodePackages, generateJobCodePackage} from '@/api/console';
import {handleRequestError} from '@/utils/errorHandler';

export const CODE_PACKAGE_STATUS = {
    notGenerated: -1,
    generating: 0,
    ready: 1
} as const;

export const CODE_PACKAGE_STATUS_TEXT: Record<CodePackageStatus, string> = {
    [CODE_PACKAGE_STATUS.notGenerated]: '未生成',
    [CODE_PACKAGE_STATUS.generating]: '生成中',
    [CODE_PACKAGE_STATUS.ready]: '已生成'
};

export type CodePackageStatus = typeof CODE_PACKAGE_STATUS[keyof typeof CODE_PACKAGE_STATUS];

type BackendCodePackageStatus = number;

interface BackendCodePackageItem {
    s_id?: string;
    status?: BackendCodePackageStatus;
    package_url?: string;
    message?: string;
}

export interface CodePackageItem {
    id: string;
    rank: number;
    node: string;
    path?: string;
    generation?: number;
    iterationFound?: number | string;
    score: number;
    status: CodePackageStatus;
    downloadUrl?: string;
    reason?: string;
}

const normalizeStatus = (status?: BackendCodePackageStatus): CodePackageStatus => {
    return Object.values(CODE_PACKAGE_STATUS).includes(status as CodePackageStatus)
        ? status as CodePackageStatus
        : CODE_PACKAGE_STATUS.notGenerated;
};

const getListFromResponse = (data: any): Record<string, BackendCodePackageItem> => {
    const list = data?.list ?? data?.data?.list;
    if (!list) {
        return {};
    }

    if (Array.isArray(list)) {
        return list.reduce((result, item) => {
            if (item?.s_id) {
                result[item.s_id] = item;
            }
            return result;
        }, {} as Record<string, BackendCodePackageItem>);
    }

    return list as Record<string, BackendCodePackageItem>;
};

const normalizePackageItem = (
    solution: InitialSolutionFileInfo,
    packageInfo: BackendCodePackageItem | undefined,
    index: number
): CodePackageItem => ({
    id: solution.s_id,
    rank: Number(solution.index ?? index + 1),
    node: solution.name || solution.s_id,
    path: solution.path,
    score: Number(solution.combined_score ?? 0),
    generation: solution.generation,
    iterationFound: solution.iteration_found,
    status: normalizeStatus(packageInfo?.status),
    downloadUrl: packageInfo?.package_url,
    reason: packageInfo?.message
});

export class TaskCodePackageStore {
    visible = false;
    loading = false;
    jobId = '';
    packages: CodePackageItem[] = [];
    solutionInfoList: InitialSolutionFileInfo[] = [];
    selectedRowKeys: string[] = [];
    statusFilters: CodePackageStatus[] = [];
    // 记录代码包已生成数量的变化，用于抽屉外轮询时提示新增生成结果。
    generatedNum = 0;
    previousGeneratedNum = 0;
    pendingGeneratedNumIncrease = 0;
    generatedNumInitialized = false;

    constructor() {
        makeAutoObservable(this, {
            filteredPackages: computed,
            selectedPackages: computed,
            selectedReadyCount: computed,
            selectedNeedGenerateCount: computed,
            isAllFilteredSelected: computed,
            isPartFilteredSelected: computed,
            generatedNumIncrease: computed,
            openDrawer: action.bound,
            setPackageQuery: action.bound,
            setSolutionInfoList: action.bound,
            closeDrawer: action.bound,
            fetchPackages: flow.bound,
            updateGeneratedNum: action.bound,
            consumeGeneratedNumIncrease: action.bound,
            resetGeneratedNum: action.bound,
            openCurrentGeneratedPackages: action.bound,
            setSelectedRowKeys: action.bound,
            togglePackage: action.bound,
            toggleAllFiltered: action.bound,
            setStatusFilters: action.bound,
            markGenerating: action.bound,
            markReady: action.bound,
            refreshPackages: action.bound,
            generatePackages: flow.bound,
            handleDownload: flow.bound,
            clearSelection: action.bound
        });
    }

    get filteredPackages() {
        return this.packages;
    }

    get selectedPackages() {
        return this.packages.filter(item => this.selectedRowKeys.includes(item.id));
    }

    get selectedReadyCount() {
        return this.selectedPackages.filter(item => item.status === CODE_PACKAGE_STATUS.ready).length;
    }

    get selectedNeedGenerateCount() {
        return this.selectedPackages
            .filter(item => item.status === CODE_PACKAGE_STATUS.notGenerated)
            .length;
    }

    get isAllFilteredSelected() {
        return this.filteredPackages.length > 0
            && this.filteredPackages.every(item => this.selectedRowKeys.includes(item.id));
    }

    get isPartFilteredSelected() {
        return !this.isAllFilteredSelected
            && this.filteredPackages.some(item => this.selectedRowKeys.includes(item.id));
    }

    get generatedNumIncrease() {
        return this.pendingGeneratedNumIncrease;
    }

    openDrawer(jobId: string, solutionInfoList: InitialSolutionFileInfo[] = []) {
        this.visible = true;
        this.setPackageQuery(jobId, solutionInfoList);
        this.clearSelection();
        void this.fetchPackages();
    }

    setPackageQuery(jobId: string, solutionInfoList: InitialSolutionFileInfo[] = []) {
        const shouldResetGeneratedNum = this.jobId !== jobId;
        this.jobId = jobId;
        this.setSolutionInfoList(solutionInfoList);

        if (shouldResetGeneratedNum) {
            this.resetGeneratedNum();
        }
    }

    setSolutionInfoList(solutionInfoList: InitialSolutionFileInfo[]) {
        this.solutionInfoList = solutionInfoList;
    }

    closeDrawer() {
        this.visible = false;
        this.statusFilters = [];
        this.clearSelection();
    }

    * fetchPackages(showLoading = true) {
        if (showLoading) {
            this.loading = true;
        }
        try {
            const sIds = this.solutionInfoList.map(item => item.s_id).filter(Boolean);
            const status = this.statusFilters.length ? this.statusFilters : undefined;
            const res: ApiResponse = yield fetchJobCodePackages({job_id: this.jobId, s_id: sIds, status});
            const generatedNum = Number(res.data?.generated_num ?? 0);
            const packageMap = getListFromResponse(res.data);

            const shouldUseBackendList = this.statusFilters.length > 0;
            const solutionInfoList = shouldUseBackendList
                ? this.solutionInfoList.filter(item => Object.prototype.hasOwnProperty.call(packageMap, item.s_id))
                : this.solutionInfoList;
            const packages = solutionInfoList.map(
                (item, index) => normalizePackageItem(item, packageMap[item.s_id], index));
            this.updateGeneratedNum(generatedNum);
            this.packages = packages;
        } catch (error) {
            // 请求失败时保留上一次列表，避免用假数据覆盖真实状态。
        } finally {
            if (showLoading) {
                this.loading = false;
            }
        }
    }

    updateGeneratedNum(generatedNum: number) {
        if (!Number.isFinite(generatedNum)) {
            return;
        }

        if (!this.generatedNumInitialized) {
            this.generatedNum = generatedNum;
            this.previousGeneratedNum = generatedNum;
            this.generatedNumInitialized = true;
            return;
        }

        this.previousGeneratedNum = this.generatedNum;
        this.generatedNum = generatedNum;
        this.pendingGeneratedNumIncrease = Math.max(generatedNum - this.previousGeneratedNum, 0);
    }

    consumeGeneratedNumIncrease() {
        const increase = this.pendingGeneratedNumIncrease;
        this.pendingGeneratedNumIncrease = 0;
        return increase;
    }

    resetGeneratedNum() {
        this.generatedNum = 0;
        this.previousGeneratedNum = 0;
        this.pendingGeneratedNumIncrease = 0;
        this.generatedNumInitialized = false;
    }

    openCurrentGeneratedPackages() {
        this.visible = true;
        this.statusFilters = [];
        void this.fetchPackages();
    }

    * generatePackages(ids: CodePackageItem[]) {
        if (!ids.length) {
            return;
        }

        this.markGenerating(ids.map(el => el.id));
        try {
            const s_id = ids.map(el => ({sid: el.id, sort: el.rank + 1}));
            yield generateJobCodePackage({job_id: this.jobId, s_id});
            yield this.fetchPackages();
        } catch (error) {
            handleRequestError({error});
        }
    }

    * handleDownload(ids: string[]) {
        const selectedPackages = this.packages.filter(item => ids.includes(item.id));
        const needGenerateIds = selectedPackages
            .filter(item => item.status === CODE_PACKAGE_STATUS.notGenerated);
        yield this.generatePackages(needGenerateIds);
    }

    refreshPackages() {
        void this.fetchPackages();
    }

    setSelectedRowKeys(keys: string[]) {
        this.selectedRowKeys = keys;
    }

    togglePackage(id: string, checked: boolean) {
        if (checked) {
            this.selectedRowKeys = Array.from(new Set([...this.selectedRowKeys, id]));
            return;
        }
        this.selectedRowKeys = this.selectedRowKeys.filter(key => key !== id);
    }

    toggleAllFiltered(checked: boolean) {
        const filteredKeys = this.filteredPackages.map(item => item.id);
        if (checked) {
            this.selectedRowKeys = Array.from(new Set([...this.selectedRowKeys, ...filteredKeys]));
            return;
        }
        this.selectedRowKeys = this.selectedRowKeys.filter(key => !filteredKeys.includes(key));
    }

    setStatusFilters(status: CodePackageStatus[]) {
        this.statusFilters = status;
    }

    markGenerating(ids: string[]) {
        this.packages = this.packages.map(item => (
            ids.includes(item.id) ? {...item, status: CODE_PACKAGE_STATUS.generating, reason: undefined} : item
        ));
    }

    markReady(ids: string[]) {
        this.packages = this.packages.map(item => (
            ids.includes(item.id) ? {...item, status: CODE_PACKAGE_STATUS.ready, reason: undefined} : item
        ));
    }

    clearSelection() {
        this.selectedRowKeys = [];
    }
}
